import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useLingui } from '@lingui/react/macro'
import { trpc } from '@/main'
import { DevLoginAsShell } from './dev-login-as-shell'

// sessionStorage key holding the list of emails the developer has logged in as this session, so they
// surface as a creatable-select datalist for quick repeat switches. Scoped to the tab (sessionStorage)
// on purpose — it's throwaway local-testing state, not something to persist across sessions.
const STORAGE_KEY = 'devLoginAsEmails'

function readSavedEmails(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((value): value is string => typeof value === 'string') : []
  } catch {
    return []
  }
}

function saveEmail(email: string): void {
  if (typeof window === 'undefined') return
  // Most-recently-used first, deduped, capped — keeps the datalist short and the last account on top.
  const next = [email, ...readSavedEmails().filter((value) => value !== email)].slice(0, 10)
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* sessionStorage unavailable (private mode / quota) — the feature still works without history. */
  }
}

// DEV ESCAPE HATCH (rendered when the session qualifies — see user-menu's showDevLoginAs): a creatable
// select that logs in as any user by email, no password (server endpoint is auth.loginAs, itself
// guarded by canDevLoginAs). Replaces the production "Switch to account" menu item so we can hop
// between accounts without logging in by hand.
export function DevLoginAs() {
  const { t } = useLingui()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Spans the whole switch (login-as call → navigation) so the button shows progress until the reload
  // takes over. No reset needed on success: the navigation below reloads the document.
  const [switching, setSwitching] = useState(false)
  const savedEmails = readSavedEmails()

  const { mutate: loginAs } = useMutation(
    trpc.auth.loginAs.mutationOptions({
      onSuccess: (user) => {
        saveEmail(email.trim())
        // Identity switch via a FULL reload (router-native `reloadDocument`, so window.location stays out
        // of our code). A client nav between whole account contexts (company admin ↔ applicant employee)
        // left stale query + mounted-component state (jobs list, collapsible toolbars, employer avatar);
        // a clean document load resets everything. `search: {}` drops the prior account's report/profile
        // params so the new identity never lands on a resource it can't access.
        navigate({ to: user.type === 'company' ? '/jobs' : '/report', search: {}, reloadDocument: true })
      },
      onError: () => setError(t`No user with that email`),
      onSettled: () => setSwitching(false),
    }),
  )

  const submit = () => {
    const value = email.trim()
    if (!value || switching) return
    setError(null)
    setSwitching(true)
    loginAs({ email: value })
  }

  return (
    <DevLoginAsShell
      label={t`Log in as (dev)`}
      error={error}
      loading={switching}
      inputProps={{
        list: 'dev-login-as-emails',
        placeholder: t`email@example.com`,
        value: email,
        disabled: switching,
        onChange: (event) => {
          setEmail(event.target.value)
          if (error) setError(null)
        },
        onKeyDown: (event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            submit()
          }
        },
      }}
      buttonProps={{ onClick: submit, disabled: switching }}
    >
      <datalist id="dev-login-as-emails">
        {savedEmails.map((saved) => (
          <option key={saved} value={saved} />
        ))}
      </datalist>
    </DevLoginAsShell>
  )
}
